'use client';

/**
 * CommandPalette Component
 *
 * A command palette / spotlight-style search overlay.
 * Triggered by ⌘K (Mac) / Ctrl+K (Windows/Linux).
 *
 * Features:
 * - Full keyboard navigation (arrows, Enter, Escape)
 * - Debounced search input
 * - Compact result tiles
 * - Quick navigation to results
 */

import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  useId,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import {
  Search,
  Loader2,
  X,
  Command,
  ArrowUp,
  ArrowDown,
  CornerDownLeft,
} from 'lucide-react';
import { useSearchAiz } from '../hooks/useSearchAiz';
import { useSearchAizSettings } from '../hooks/useSearchAizSettings';
import { getTileComponent } from '../tiles';
import { navigateToSafeUrl } from '../utils/safeNavigation';
import type { SearchResult, TileAction } from '../types';
import { useModalA11y } from '@/lib/a11y/useModalA11y';
import { mainContentFocusRef } from '@/lib/a11y/mainContentFocusRef';

// =============================================================================
// TYPES
// =============================================================================

export interface CommandPaletteProps {
  /** Whether the palette is open */
  isOpen: boolean;
  /** Callback to close the palette */
  onClose: () => void;
  /** Callback when a result is selected */
  onSelect?: (result: SearchResult) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Additional CSS classes */
  className?: string;
}

// =============================================================================
// KEYBOARD SHORTCUT HINT
// =============================================================================

function KeyboardHint({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="px-1.5 py-0.5 text-xs font-mono bg-slate-800 border border-slate-700 rounded text-slate-400">
      {children}
    </kbd>
  );
}

function ShortcutHints() {
  return (
    <div className="flex items-center gap-4 text-xs text-slate-500">
      <span className="flex items-center gap-1">
        <KeyboardHint><ArrowUp className="w-3 h-3" /></KeyboardHint>
        <KeyboardHint><ArrowDown className="w-3 h-3" /></KeyboardHint>
        <span>Navigate</span>
      </span>
      <span className="flex items-center gap-1">
        <KeyboardHint><CornerDownLeft className="w-3 h-3" /></KeyboardHint>
        <span>Select</span>
      </span>
      <span className="flex items-center gap-1">
        <KeyboardHint>Esc</KeyboardHint>
        <span>Close</span>
      </span>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function CommandPalette({
  isOpen,
  onClose,
  onSelect,
  placeholder = 'Search...',
  className,
}: CommandPaletteProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();
  const { settings } = useSearchAizSettings();

  // Get sources from settings
  const sources = useMemo(
    () => settings.sources.default,
    [settings.sources.default]
  );

  // Search state
  const {
    query,
    isSearching,
    results,
    error,
    notices,
    selectedIndex,
    search,
    clear,
    navigateSelection,
    activateSelected,
  } = useSearchAiz({
    sources,
    pattern: 'unified',
    limit: settings.results.perPage,
    rerank: settings.ai.rerank.enabled ? settings.ai.rerank : undefined,
    debounceMs: 200,
    onResultSelect: onSelect,
  });
  const activeResult = selectedIndex >= 0 ? results[selectedIndex] : undefined;
  const optionId = (resultId: string) => `${listboxId}-option-${encodeURIComponent(resultId)}`;

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Clear and close
  useEffect(() => {
    if (!isOpen) {
      clear();
    }
  }, [isOpen, clear]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current && selectedIndex >= 0) {
      const selectedEl = listRef.current.children[selectedIndex] as HTMLElement;
      if (selectedEl) {
        selectedEl.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  /**
   * Handle keyboard navigation
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          navigateSelection('down');
          break;
        case 'ArrowUp':
          e.preventDefault();
          navigateSelection('up');
          break;
        case 'Enter':
          e.preventDefault();
          activateSelected();
          onClose();
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    },
    [navigateSelection, activateSelected, onClose]
  );

  /**
   * Handle result click
   */
  const handleResultClick = useCallback(
    (result: SearchResult) => {
      onSelect?.(result);
      navigateToSafeUrl(result.url);
      onClose();
    },
    [onSelect, onClose]
  );

  /**
   * Handle tile action
   */
  const handleTileAction = useCallback(
    (action: TileAction) => {
      if (action.type === 'view' && action.url) {
        if (navigateToSafeUrl(action.url)) {
          onClose();
        }
      } else if (action.type === 'copyLink' && action.url) {
        navigator.clipboard.writeText(action.url);
      }
    },
    [onClose]
  );

  // Dialog semantics, Escape outside the input and focus return (the input handles its own Escape and focuses itself).
  const { ref: dialogRef, props: dialogProps } = useModalA11y({ onClose, open: isOpen, initialFocusRef: inputRef, fallbackFocusRef: mainContentFocusRef });

  // Don't render if not open
  if (!isOpen) return null;

  const content = (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" role="presentation" onClick={onClose} />

      {/* Palette */}
      <div
        ref={dialogRef}
        {...dialogProps}
        aria-label="Command palette"
        className={cn(
          'relative w-full max-w-2xl mx-4',
          'bg-slate-900 border border-slate-700 rounded-xl shadow-2xl',
          'overflow-hidden',
          className
        )}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-800">
          {isSearching ? (
            <Loader2 className="w-5 h-5 text-slate-500 animate-spin" />
          ) : (
            <Search className="w-5 h-5 text-slate-500" />
          )}

          <input aria-label="Search"
            role="combobox"
            aria-expanded={true}
            aria-controls={listboxId}
            aria-activedescendant={activeResult ? optionId(activeResult.id) : undefined}
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => search(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className={cn(
              'flex-1 bg-transparent text-slate-200 placeholder-slate-500',
              'text-lg outline-none'
            )}
            autoComplete="off"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
          />

          {query && (
            <button aria-label="Clear search"
              onClick={() => clear()}
              className="p-1 rounded hover:bg-slate-800 text-slate-500 hover:text-slate-300"
            >
              <X className="w-4 h-4" />
            </button>
          )}

          <div className="flex items-center gap-1 text-slate-600">
            <Command className="w-4 h-4" />
            <span className="text-xs">K</span>
          </div>
        </div>

        {/* Results */}
        <div className="max-h-[50vh] overflow-y-auto">
          {!error && !isSearching && notices.length > 0 && (
            <div data-testid="search-aiz-notices" role="status" className="px-4 py-2 space-y-1 text-xs text-amber-300">
              {notices.map((notice) => <p key={notice}>{notice}</p>)}
              {results.length === 0 && <p>Nothing was found in the sources that could be searched.</p>}
            </div>
          )}

          {error && (
            <div className="px-4 py-6 text-center text-red-400">
              <p className="text-sm">{error.message}</p>
            </div>
          )}

          {!error && notices.length === 0 && results.length === 0 && query.length >= 2 && !isSearching && (
            <div className="px-4 py-8 text-center text-slate-500">
              <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No results found for "{query}"</p>
            </div>
          )}

          {!error && notices.length === 0 && results.length === 0 && query.length < 2 && (
            <div className="px-4 py-8 text-center text-slate-500">
              <p className="text-sm">Type to search...</p>
            </div>
          )}

          <div
            ref={listRef}
            id={listboxId}
            role="listbox"
            className={results.length > 0 ? 'p-2 space-y-1' : undefined}
          >
            {results.map((result, index) => {
              const TileComponent = getTileComponent(result.type);

              return (
                <TileComponent
                  key={result.id}
                  result={result}
                  mode="compact"
                  showScore={settings.results.showScores}
                  showSource={settings.results.showSources}
                  highlighted={index === selectedIndex}
                  listboxOption={{ id: optionId(result.id), selected: index === selectedIndex }}
                  onClick={() => handleResultClick(result)}
                  onAction={handleTileAction}
                />
              );
            })}
          </div>
        </div>

        {/* Footer with keyboard hints */}
        <div className="px-4 py-2 border-t border-slate-800 flex items-center justify-between">
          <ShortcutHints />

          {results.length > 0 && (
            <span className="text-xs text-slate-500">
              {results.length} result{results.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
    </div>
  );

  // Render in portal to escape any parent stacking contexts
  if (typeof document !== 'undefined') {
    return createPortal(content, document.body);
  }

  return content;
}

// =============================================================================
// HOOK FOR COMMAND PALETTE
// =============================================================================

export interface UseCommandPaletteOptions {
  /** Keyboard shortcut key (default: 'k') */
  shortcutKey?: string;
  /** Whether to use Cmd on Mac (default: true) */
  useCmd?: boolean;
}

/**
 * Hook to manage command palette open state with keyboard shortcut
 */
export function useCommandPalette(options: UseCommandPaletteOptions = {}) {
  const { shortcutKey = 'k', useCmd = true } = options;
  const [isOpen, setIsOpen] = useState(false);

  // Listen for keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for Cmd/Ctrl + K
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modifier = useCmd && isMac ? e.metaKey : e.ctrlKey;

      if (modifier && e.key.toLowerCase() === shortcutKey) {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [shortcutKey, useCmd]);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  return {
    isOpen,
    open,
    close,
    toggle,
  };
}

export default CommandPalette;
